/**
 * The properties module is for storing all SILA properties
 * 
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */
import Vue from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import api from '@/api'
import {
    VUEX_PROPERTIES_INIT,
    VUEX_PROPERTIES_INITIALIZED,
    VUEX_PROPERTIES_SET_ALL,

    VUEX_PROPERTIES_GET_BLACKOUT_DATES,
    VUEX_PROPERTIES_GET_BLACKOUT_DATES_SUCCESS,
    VUEX_PROPERTIES_GET_BLACKOUT_DATES_FAILURE } from '@/store/constants/config/properties' 



/**
 * Properties module state
 * @const
 */
const state = {
    initialized: false,
    
    /**
     * Non-conforming props I have to work on more.
     */
    absenceTypes:                   [],
    absenceStatuses:                [],
    timeOffTypes:                   [],
    timeOffDeptStatuses:            [],
    timeOffHRStatuses:              [],
    orgBlackoutDates:               [],
    documentUsedAsTypes:            [],

    /**
     * TODO: Add to config on server
     */
    rowsPerPageItems:                [10, 20, 50, 100, 200]
}

/**
 * Properties module getters
 * @const
 */
const getters = {
    propertiesAreLoaded: state => state.initialized,
    

    /**
     * This getter returns the list item that matched the id passed into it
     * @param whichList - state.jobCodes, jobCodes.jobStatuses, etc 
     * @param id - id of event
     */
    getJobEnumItem:(state) => (whichList, val) => {
        let idx = state[whichList].findIndex(x => x.value === val)
        return state[whichList][idx]
    },


    /**
     * This getter returns the list item that matched the id passed into it
     * But doesn't have to have be an object with a 'value'
     * @param whichList - state.jobCodes, jobCodes.jobStatuses, etc 
     * @param id - id of event
     */
    getEnumItem:(state) => (whichList, val) => {
        let idx = state[whichList][val - 1]
        return idx
    },

    /**
     * This getter returns value of the 'key' from the returned list
     * @param whichList - state.jobCodes, jobCodes.jobStatuses, etc 
     * @param id - return obj with matching id
     * @param key - return value of key on object
     */
    getJobEnumItemValue:(state, getters, rootState) => (whichList, val, key) => {
        // if (rootState.jobs.jobLoaded === false) return null
        let item = getters.getJobEnumItem(whichList, val)
        return item ? item[key] : null
    },


    getJobEnumItemByKey:(state) => (whichList, val, sortKey) => {
        let idx = state[whichList].findIndex(x => x[sortKey] === val)
        return state[whichList][idx]
    },
    
    getJobEnumItemValueByKey:(state, getters, rootState) => (whichList, val, sortKey, returnKey) => {
        let item = getters.getJobEnumItemByKey(whichList, val, sortKey, returnKey )
        return item ? item[returnKey] : null
    },
}

/**
 * Properties module action definitions
 * @const
 */
const actions = {
    [VUEX_PROPERTIES_INIT]: async ({ dispatch, commit }) => {
        commit(VUEX_PROPERTIES_INIT)

        await api.get('/config/properties').then((response) => {
          commit(VUEX_PROPERTIES_SET_ALL, response.data.data)
        })

        dispatch(VUEX_PROPERTIES_GET_BLACKOUT_DATES)
        commit(VUEX_PROPERTIES_INITIALIZED)
    },

    [VUEX_PROPERTIES_GET_BLACKOUT_DATES]: ({ commit }) => {
        api.get(`/blackout_dates`).then(async(response) => {
          await commit(VUEX_PROPERTIES_GET_BLACKOUT_DATES_SUCCESS, response.data.data)
        }).catch((err) => {
            console.error(err)
            // commit(VUEX_PROPERTIES_GET_BLACKOUT_DATES_FAILURE, err)
        })
    }
}

/**
 * Properties module mutation definitions
 * @const
 */
const mutations = {
	[VUEX_PROPERTIES_INIT]: () => {},
	
    [VUEX_PROPERTIES_INITIALIZED]: (state) => {
		state.initialized = true
	},

    [VUEX_PROPERTIES_SET_ALL]: (state, payload) => {
		Object.keys(payload).forEach( function(domain, index) {

            let keyList = Object.keys(payload[domain]);
            
            if (parseInt(keyList[0]) === 0) { // really bad test for numeric array vs dict
                let key = `${domain}`.replace(/_\w/g, (m) => m[1].toUpperCase() )
                Vue.set(state, key, cloneDeep(payload[domain]))
            } else {
                Object.keys(payload[domain]).forEach( function(propertyList, index) {
                    let key = `${domain}_${propertyList}`.replace(/_\w/g, (m) => m[1].toUpperCase() )
                    Vue.set(state, key, cloneDeep(payload[domain][propertyList]))
                })
            }			
        })
        

        /**
         * Non-conforming props I have to work on more.
         */
        state.documentUsedAsTypes = payload.document.used_as
        state.absenceTypes = payload.absence_notice.type
        state.absenceStatus = payload.absence_notice.status
        state.timeOffTypes = payload.time_off_request.type
        state.timeOffStatus = payload.time_off_request.status
        state.orgBlackoutDates = payload.calendar_blackout_dates
    },

    [VUEX_PROPERTIES_GET_BLACKOUT_DATES_SUCCESS]: (state, payload) => {
        state.orgBlackoutDates = payload.blackout_dates
    }
}


/**
 * The Properties module of our Vuex store.
 * @module store/modules/properties
 */
export default {
  state,
  getters,
  actions,
  mutations,
}
